import {isCheckInEligible} from "../../lib/appointmentPolicy";
import { AppointmentMessagingStatus, messagingText } from "../../components/AppointmentMessagingStatus";
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import { useClinicLinks } from "./ClinicAdminPages";
import { CommonCalendar } from "../../components/CommonCalendar";
import { ActionPromptModal } from "../../components/ActionPromptModal";
import { ConsultationControl } from "../../components/ConsultationControl";
import { AppointmentSlotPicker, SlotRescheduleModal } from "../../components/AppointmentSlotPicker";
import api, { apiErrorMessage } from "../../lib/api";
import { toast } from "sonner";
import { useCan } from "../../lib/permissions";
import { PatientSearchSelect } from "../../components/PatientSearchSelect";
import { doctorOffersService, effectiveBookingPrice, normalizeBookingService, normalizeDoctorServiceAssignment } from "../../lib/bookingOptions";
import {
  Search, Plus, RefreshCw, Calendar, ListIcon, Filter, X, Eye, Pencil,
  CheckCircle2, XCircle, Clock, ArrowLeft, ChevronRight, Repeat, AlertTriangle,
  Stethoscope, User as UserIcon, Briefcase, Receipt, ClipboardList, Download,
  PhoneCall as PhoneIcon,
} from "lucide-react";

const STATUS_PILL = {
  Scheduled: "bg-mg-medical/10 text-mg-medical",
  Confirmed: "bg-emerald-100 text-emerald-700",
  "Checked In": "bg-amber-100 text-amber-700",
  "In Consultation": "bg-purple-100 text-purple-700",
  Completed: "bg-slate-100 text-slate-700",
  Cancelled: "bg-red-100 text-red-700",
  "No Show": "bg-red-100 text-red-700",
  Expired: "bg-amber-50 text-amber-700",
  Rescheduled: "bg-purple-50 text-purple-600",
  Deleted: "bg-red-50 text-red-400 line-through",
};
const SOURCE_LABEL = { online: "Online", manual: "Manual", followup: "Follow-up", assistant: "AI", public: "Public" };

const fmtDT = (s) => { try { return new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return s; } };
const fmtT = (s) => { try { return new Date(s).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); } catch { return s; } };

// ============================================================
// LIST + CALENDAR PAGE
// ============================================================
export function ClinicAppointmentsListPage() {
  const links = useClinicLinks();
  const [mode, setMode] = useState("list"); // list | calendar
  const [calView, setCalView] = useState("week");
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkReasonModal, setBulkReasonModal] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    q: "", status_filter: "", source: "", doctor_id: "", service_id: "",
    date_from: "", date_to: "", payment_status: "", slot_window: "",
    has_encounter: "", is_followup: "", priority: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctorServices, setDoctorServices] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const canBulk = useCan("appointments.bulk_actions");
  const canExport = useCan("appointments.export");
  const canCreate = useCan("appointments.create");

  useEffect(() => {
    api.get("/visits/options").then(({ data }) => {
      setDoctors(data.doctors || []);
      setServices((data.services || []).map(normalizeBookingService));
      setPatients(data.patients || []);
      setDoctorServices((data.doctor_services || []).map(normalizeDoctorServiceAssignment));
    }).catch((error) => toast.error(apiErrorMessage(error, "Appointment filters could not be loaded")));
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) params.append(k, v); });
      const r = await api.get(`/clinic/appointments-x?${params.toString()}`);
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    setLoading(false);
  }, [filters]);

  const loadCalendar = useCallback(async (range) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (range?.from) params.append("date_from", range.from);
      if (range?.to) params.append("date_to", range.to);
      if (filters.doctor_id) params.append("doctor_id", filters.doctor_id);
      if (filters.status_filter) params.append("status_filter", filters.status_filter);
      const r = await api.get(`/clinic/appointments-x/calendar?${params.toString()}`);
      setEvents(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    setLoading(false);
  }, [filters.doctor_id, filters.status_filter]);

  useEffect(() => {
    if (mode === "list") loadList();
    else loadCalendar({
      from: new Date(Date.now() - 7 * 86400_000).toISOString().slice(0, 10),
      to: new Date(Date.now() + 35 * 86400_000).toISOString().slice(0, 10),
    });
  }, [mode, loadList, loadCalendar]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({
    q: "", status_filter: "", source: "", doctor_id: "", service_id: "",
    date_from: "", date_to: "", payment_status: "", slot_window: "",
    has_encounter: "", is_followup: "", priority: "",
  });

  const toggleSelect = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const runBulk = async (action) => {
    if (!selected.size) return toast.error("No appointments selected");
    setBulkReasonModal({ action, ids: [...selected] });
  };

  const submitBulk = async (reason) => {
    if (!bulkReasonModal) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      toast.error("Reason is required");
      return;
    }
    setBulkSubmitting(true);
    try {
      const r = await api.post("/clinic/appointments-x/bulk", {
        appointment_ids: bulkReasonModal.ids,
        action: bulkReasonModal.action,
        reason: trimmedReason,
      });
      toast.success(`${r.data.ok.length} ok, ${r.data.failed.length} failed`);
      setSelected(new Set());
      setBulkReasonModal(null);
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk failed");
    }
    setBulkSubmitting(false);
  };

  const exportCsv = () => {
    if (!rows.length) return toast.error("No data");
    const cols = ["appointment_id", "appointment_datetime", "duration_minutes", "status", "source",
                  "patient.name", "patient.phone", "doctor.name", "service.name",
                  "payment_status", "has_encounter", "priority"];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
      return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
    };
    const get = (r, path) => path.split(".").reduce((a, k) => (a ? a[k] : ""), r);
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(get(r, c))).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `medigenie-appointments-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  const activeFilterCount = Object.values(filters).filter((v) => v !== "" && v != null).length;
  const nav = useNavigate();

  return (
    <DashboardLayout links={links} title="Appointments">
      <div className="mg-card mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="mg-input pl-9 w-full" placeholder="Search patient name / phone…"
              value={filters.q} onChange={(e) => setFilter("q", e.target.value)} data-testid="appts-search" />
          </div>
          <button onClick={() => setShowFilters(!showFilters)}
            className={`mg-btn-secondary text-sm relative ${activeFilterCount > 0 ? "border-mg-medical text-mg-medical" : ""}`}
            data-testid="appts-filters-toggle">
            <Filter className="w-4 h-4" /> Filters
            {activeFilterCount > 0 && <span className="absolute -top-1 -right-1 bg-mg-medical text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">{activeFilterCount}</span>}
          </button>
          <div className="flex border border-slate-200 rounded overflow-hidden text-xs ml-auto">
            <button onClick={() => setMode("list")}
              className={`px-3 py-1.5 ${mode === "list" ? "bg-mg-medical text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="appts-mode-list"><ListIcon className="w-4 h-4 inline" /> List</button>
            <button onClick={() => setMode("calendar")}
              className={`px-3 py-1.5 ${mode === "calendar" ? "bg-mg-medical text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="appts-mode-calendar"><Calendar className="w-4 h-4 inline" /> Calendar</button>
          </div>
          {canExport && mode === "list" && (
            <button onClick={exportCsv} className="mg-btn-secondary text-sm" data-testid="appts-export"><Download className="w-4 h-4" /> CSV</button>
          )}
          {canCreate && (
            <button onClick={() => setCreateOpen(true)} className="mg-btn-primary text-sm" data-testid="create-appointment-btn">
              <Plus className="w-4 h-4" /> New appointment
            </button>
          )}
          <button onClick={mode === "list" ? loadList : () => loadCalendar()} className="mg-btn-secondary text-sm" data-testid="appts-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="appts-filters-panel">
            <SelectField label="Status" value={filters.status_filter} onChange={(v) => setFilter("status_filter", v)} testid="filter-status"
              options={["", "Scheduled", "Confirmed", "Checked In", "In Consultation", "Completed", "Cancelled", "No Show", "Rescheduled", "Expired"]} />
            <SelectField label="Source" value={filters.source} onChange={(v) => setFilter("source", v)} testid="filter-source"
              options={["", "online", "manual", "followup", "assistant", "public"]} />
            <SelectField label="Doctor" value={filters.doctor_id} onChange={(v) => setFilter("doctor_id", v)} testid="filter-doctor"
              options={[["", "All"], ...doctors.map((d) => [d.doctor_id, d.name])]} />
            <SelectField label="Service" value={filters.service_id} onChange={(v) => setFilter("service_id", v)} testid="filter-service"
              options={[["", "All"], ...services.map((s) => [s.service_id, s.name])]} />
            <FieldWrap label="From date">
              <input type="date" className="mg-input" value={filters.date_from} onChange={(e) => setFilter("date_from", e.target.value)} data-testid="filter-from" />
            </FieldWrap>
            <FieldWrap label="To date">
              <input type="date" className="mg-input" value={filters.date_to} onChange={(e) => setFilter("date_to", e.target.value)} data-testid="filter-to" />
            </FieldWrap>
            <SelectField label="Payment" value={filters.payment_status} onChange={(v) => setFilter("payment_status", v)} testid="filter-payment"
              options={["", "paid", "partial", "pending", "refunded", "none"]} />
            <SelectField label="Slot window" value={filters.slot_window} onChange={(v) => setFilter("slot_window", v)} testid="filter-slot-window"
              options={["", "morning", "afternoon", "evening"]} />
            <SelectField label="Has encounter" value={filters.has_encounter} onChange={(v) => setFilter("has_encounter", v)} testid="filter-encounter"
              options={[["", "Any"], ["true", "Yes"], ["false", "No"]]} />
            <SelectField label="From follow-up" value={filters.is_followup} onChange={(v) => setFilter("is_followup", v)} testid="filter-followup"
              options={[["", "Any"], ["true", "Yes"], ["false", "No"]]} />
            <SelectField label="Priority" value={filters.priority} onChange={(v) => setFilter("priority", v)} testid="filter-priority"
              options={["", "low", "normal", "high", "urgent"]} />
            <div className="flex items-end">
              <button onClick={clearFilters} className="mg-btn-secondary text-sm w-full justify-center" data-testid="filters-clear">
                <X className="w-4 h-4" /> Clear all
              </button>
            </div>
          </div>
        )}
      </div>

      {mode === "list" && selected.size > 0 && canBulk && (
        <div className="mg-card mb-4 bg-mg-medical/5 border-mg-medical/20" data-testid="appt-bulk-bar">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold text-mg-navy">{selected.size} selected</div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => runBulk("check_in")} className="mg-btn-secondary text-xs" data-testid="appt-bulk-checkin">Check in</button>
              <button onClick={() => runBulk("mark_completed")} className="mg-btn-secondary text-xs" data-testid="appt-bulk-complete">Complete</button>
              <button onClick={() => runBulk("mark_no_show")} className="mg-btn-secondary text-xs" data-testid="appt-bulk-noshow">No show</button>
              <button onClick={() => runBulk("cancel")} className="mg-btn-secondary text-xs" data-testid="appt-bulk-cancel">Cancel</button>
              <button onClick={() => runBulk("soft_delete")} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-700 bg-red-50" data-testid="appt-bulk-delete">Delete</button>
              <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500">Clear</button>
            </div>
          </div>
        </div>
      )}

      <ActionPromptModal
        open={Boolean(bulkReasonModal)}
        title={bulkReasonModal ? `Reason for bulk ${bulkReasonModal.action}` : "Bulk action reason"}
        label="Reason"
        placeholder="Bulk action"
        initialValue="Bulk action"
        confirmLabel="Apply"
        busy={bulkSubmitting}
        onCancel={() => setBulkReasonModal(null)}
        onConfirm={submitBulk}
      />
      <CreateAppointmentModal
        open={createOpen} patients={patients} doctors={doctors} services={services} doctorServices={doctorServices}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); setMode("list"); loadList(); }}
      />

      {mode === "list" && (
        <div className="mg-card overflow-x-auto p-0">
          <table className="w-full text-sm" data-testid="appts-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <tr>
                {canBulk && <th className="w-8 px-3 py-3"><input type="checkbox" onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.appointment_id)) : new Set())} data-testid="appts-select-all" /></th>}
                <th className="px-2 py-3">When</th>
                <th>Patient</th>
                <th>Doctor</th>
                <th>Service</th>
                <th>Source</th>
                <th>Status</th>
                <th>Pay</th>
                <th className="text-right">View</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="9" className="text-center text-slate-400 py-8">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan="9" className="text-center text-slate-400 py-8">No appointments found.</td></tr>}
              {!loading && rows.map((r) => (
                <tr key={r.appointment_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`appt-row-${r.appointment_id}`}>
                  {canBulk && <td className="px-3 py-3"><input type="checkbox" checked={selected.has(r.appointment_id)} onChange={() => toggleSelect(r.appointment_id)} data-testid={`appt-select-${r.appointment_id}`} /></td>}
                  <td className="px-2 py-3">
                    <div className="font-semibold text-mg-navy">{fmtDT(r.appointment_datetime)}</div>
                    <div className="text-[10px] text-slate-500">{r.duration_minutes} min</div>
                  </td>
                  <td>
                    <div className="font-semibold text-mg-navy text-sm">{r.patient?.name || "—"}</div>
                    <div className="text-[10px] text-slate-500">{r.patient?.phone || ""}</div>
                  </td>
                  <td className="text-xs">{r.doctor?.name || "—"}</td>
                  <td className="text-xs">
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full inline-block" style={{ background: r.service?.calendar_color || r.service?.color || "#2090CF" }} />
                      {r.service?.name || "—"}
                    </div>
                  </td>
                  <td className="text-xs">{SOURCE_LABEL[r.source] || (r.source || "—")}</td>
                  <td><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_PILL[r.status] || "mg-status"}`}>{r.status}</span></td>
                  <td className="text-xs">
                    {r.payment_status === "paid" && <span className="text-emerald-600">Paid</span>}
                    {r.payment_status === "partial" && <span className="text-amber-600">Partial</span>}
                    {r.payment_status === "pending" && <span className="text-red-500">Due</span>}
                    {r.payment_status === "none" && <span className="text-slate-400">—</span>}
                  </td>
                  <td className="text-right">
                    <Link to={`/clinic-admin/appointments-x/${r.appointment_id}`} className="p-1.5 hover:bg-slate-100 rounded inline-block" data-testid={`appt-view-${r.appointment_id}`}>
                      <ChevronRight className="w-4 h-4 text-mg-medical" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mode === "calendar" && (
        <CommonCalendar events={events} view={calView} loading={loading}
          onViewChange={setCalView} onRangeChange={loadCalendar}
          onSelectEvent={(e) => nav(`/clinic-admin/appointments-x/${e.id}`)} />
      )}
    </DashboardLayout>
  );
}

function FieldWrap({ label, children }) {
  return (
    <div>
      <label className="text-xs text-slate-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid, disabled = false }) {
  return (
    <FieldWrap label={label}>
      <select className="mg-input w-full text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled}>
        {options.map((o) => {
          const [v, lab] = Array.isArray(o) ? o : [o, o ? o[0].toUpperCase() + o.slice(1) : "All"];
          return <option key={v} value={v}>{lab || "All"}</option>;
        })}
      </select>
    </FieldWrap>
  );
}

// ============================================================
// DETAIL PAGE
// ============================================================
export function ClinicAppointmentDetailPage() {
  const links = useClinicLinks();
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [actionReasonModal, setActionReasonModal] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleBusy, setRescheduleBusy] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const canEdit = useCan("appointments.edit");
  const canCancel = useCan("appointments.cancel");
  const canReschedule = useCan("appointments.reschedule");
  const canCheckIn = useCan("appointments.check_in");

  const load = useCallback(async () => {
    try { const r = await api.get(`/clinic/appointments-x/${id}/detail`); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const doAction = async (path, label, reason) => {
    setActionBusy(true);
    try {
      await api.post(`/clinic/appointments-x/${id}/${path}`, { reason: reason || null });
      toast.success(label);
      setActionReasonModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setActionBusy(false);
  };

  if (!data) return <DashboardLayout links={links} title="Appointment"><div className="text-slate-500">Loading…</div></DashboardLayout>;
  const isTerminal = ["Completed", "Cancelled", "No Show", "Rescheduled", "Expired", "Deleted"].includes(data.status);

  return (
    <DashboardLayout links={links} title="Appointment">
      {data.consultation_mode === "online" && !isTerminal && <div className="mg-card mb-4"><ConsultationControl appointmentId={data.appointment_id} allowConfigure /></div>}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <button onClick={() => nav("/clinic-admin/appointments-x")} className="mg-btn-secondary text-sm" data-testid="appt-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>

      <div className="mg-card mb-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-xl shrink-0 flex items-center justify-center" style={{ background: (data.service?.calendar_color || "#2090CF") + "20", color: data.service?.calendar_color || "#2090CF" }}>
            <Calendar className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-2xl font-bold text-mg-navy" data-testid="appt-detail-when">{fmtDT(data.appointment_datetime)}</h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_PILL[data.status] || ""}`} data-testid="appt-status-badge">{data.status}</span>
              {data.source && <span className="text-[10px] uppercase bg-mg-medical/10 text-mg-medical px-2 py-0.5 rounded-full">{SOURCE_LABEL[data.source] || data.source}</span>}
            </div>
            <div className="text-sm text-slate-600 mt-2 flex flex-wrap gap-4">
              <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /><strong>{data.patient?.name || "—"}</strong> · {data.patient?.phone || ""}</span>
              <span className="inline-flex items-center gap-1"><Stethoscope className="w-3 h-3" />{data.doctor?.name || "—"}</span>
              <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" />{data.service?.name || "—"} · {data.duration_minutes} min</span>
            </div>
            {data.notes && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{data.notes}</p>}
          </div>
        </div>
      </div>

      <AppointmentMessagingStatus delivery={data.notification_delivery} appointmentId={id} canRetry={canEdit && !["Deleted", "Rescheduled"].includes(data.status)} onUpdated={() => load()} />

      {(!isTerminal || data.status === "Expired") && (
        <div className="mg-card mb-4">
          <h3 className="font-bold text-mg-navy mb-3 text-sm uppercase tracking-wider">Actions</h3>
          <div className="flex flex-wrap gap-2" data-testid="appt-actions">
            {canCheckIn && isCheckInEligible(data.status) && (
              <button onClick={() => setActionReasonModal({ path: "check-in", label: "Checked in" })} className="mg-btn-primary text-sm" data-testid="appt-checkin">
                <CheckCircle2 className="w-4 h-4" /> Check in
              </button>
            )}
            {canEdit && data.status !== "Expired" && (
              <button onClick={() => setActionReasonModal({ path: "complete", label: "Completed" })} className="mg-btn-secondary text-sm" data-testid="appt-complete">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Complete
              </button>
            )}
            {canEdit && (
              <button onClick={() => setActionReasonModal({ path: "no-show", label: "Marked no-show" })} className="mg-btn-secondary text-sm" data-testid="appt-noshow">
                <XCircle className="w-4 h-4 text-red-500" /> No show
              </button>
            )}
            {canCancel && data.status !== "Expired" && (
              <button onClick={() => setActionReasonModal({ path: "cancel", label: "Cancelled" })} className="mg-btn-secondary text-sm" data-testid="appt-cancel">
                <X className="w-4 h-4" /> Cancel
              </button>
            )}
            {canReschedule && data.status !== "Expired" && (
              <button onClick={() => setShowReschedule(true)} className="mg-btn-secondary text-sm" data-testid="appt-reschedule">
                <Repeat className="w-4 h-4" /> Reschedule
              </button>
            )}
            {canEdit && data.status !== "Expired" && (
              <button onClick={() => setShowEdit(true)} className="mg-btn-secondary text-sm" data-testid="appt-edit">
                <Pencil className="w-4 h-4" /> Edit Details
              </button>
            )}
          </div>
        </div>
      )}

      {showEdit && (
        <EditAppointmentModal 
          appt={data} 
          onClose={() => setShowEdit(false)} 
          onSave={() => { setShowEdit(false); load(); }} 
        />
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy mb-3 flex items-center gap-2"><Receipt className="w-4 h-4" /> Invoice</h3>
          {data.invoice ? (
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Status</span><span className="font-semibold">{data.invoice.status}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Amount</span><span>₹{(data.invoice.amount || 0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Paid</span><span className="text-emerald-600">₹{(data.invoice.paid_amount || 0).toLocaleString()}</span></div>
            </div>
          ) : <div className="text-slate-400 text-sm py-2">No invoice yet.</div>}
        </div>
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Encounter</h3>
          {data.encounter ? (
            <div className="text-sm space-y-1">
              <div className="text-slate-500">Status: <span className="font-semibold text-mg-navy">{data.encounter.status}</span></div>
              {data.encounter.diagnosis && <div className="text-slate-600 mt-1">Dx: {data.encounter.diagnosis}</div>}
            </div>
          ) : <div className="text-slate-400 text-sm py-2">No encounter yet.</div>}
        </div>
        {data.followup && (
          <div className="mg-card lg:col-span-2 bg-mg-medical/5 border-mg-medical/20">
            <h3 className="font-bold text-mg-navy mb-2 flex items-center gap-2"><PhoneIcon className="w-4 h-4 text-mg-medical" /> Created from follow-up</h3>
            <div className="text-sm text-slate-600">{data.followup.reason}</div>
            <Link to={`/clinic-admin/followups/${data.followup.followup_id}`} className="text-mg-medical text-xs hover:underline mt-1 inline-block">View follow-up →</Link>
          </div>
        )}
      </div>

      {showReschedule && <SlotRescheduleModal appointment={data} busy={rescheduleBusy} onClose={() => setShowReschedule(false)} onSubmit={async (newDatetime, reason) => { setRescheduleBusy(true); try { await api.post(`/clinic/appointments-x/${id}/reschedule`, { new_datetime: newDatetime, reason: reason || null }); toast.success("Rescheduled"); setShowReschedule(false); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setRescheduleBusy(false); } }} />}
      <ActionPromptModal
        open={Boolean(actionReasonModal)}
        title={actionReasonModal ? `${actionReasonModal.label} reason (optional)` : "Action reason"}
        label="Reason"
        placeholder=""
        initialValue=""
        confirmLabel={actionReasonModal?.label || "Submit"}
        busy={actionBusy}
        onCancel={() => setActionReasonModal(null)}
        onConfirm={(reason) => {
          if (!actionReasonModal) return;
          void doAction(actionReasonModal.path, actionReasonModal.label, reason);
        }}
      />
    </DashboardLayout>
  );
}

function EditAppointmentModal({ appt, onClose, onSave }) {
  const [form, setForm] = useState({
    doctor_id: appt.doctor_id || "",
    service_id: appt.service_id || "",
    duration_minutes: appt.duration_minutes || 30,
    status: appt.status || "Scheduled",
    notes: appt.notes || ""
  });
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/clinic/doctors-x").then(r => setDoctors(r.data.items || r.data)).catch(()=>{});
    api.get("/clinic/services-x").then(r => setServices(r.data)).catch(()=>{});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/clinic/appointments-x/${appt.appointment_id}`, form);
      toast.success("Appointment updated");
      onSave();
    } catch(err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-mg-navy text-lg">Edit Details</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Doctor</label>
              <select className="mg-input w-full text-sm" value={form.doctor_id} onChange={e=>setForm({...form, doctor_id: e.target.value})} required>
                <option value="">Select Doctor</option>
                {doctors.map(d => <option key={d.doctor_id} value={d.doctor_id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Service</label>
              <select className="mg-input w-full text-sm" value={form.service_id} onChange={e=>setForm({...form, service_id: e.target.value})} required>
                <option value="">Select Service</option>
                {services.map(s => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Status</label>
              <select className="mg-input w-full text-sm" value={form.status} onChange={e=>setForm({...form, status: e.target.value})} required>
                <option value="Scheduled">Scheduled</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Checked In">Checked In</option>
                <option value="In Consultation">In Consultation</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
                <option value="No Show">No Show</option>
                {form.status === "Expired" && <option value="Expired" disabled>Expired</option>}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Duration (mins)</label>
              <input type="number" min="5" className="mg-input w-full text-sm" value={form.duration_minutes} onChange={e=>setForm({...form, duration_minutes: Number(e.target.value)})} />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Notes / Reason</label>
              <textarea className="mg-input w-full text-sm" rows={2} value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="mg-btn-secondary text-sm px-6">Cancel</button>
            <button type="submit" disabled={busy} className="mg-btn-primary text-sm px-6">{busy ? "Saving..." : "Save Changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateAppointmentModal({ open, patients, doctors, services, doctorServices, onClose, onCreated }) {
  const [form, setForm] = useState({ patient_id: "", doctor_id: "", service_id: "", consultation_mode: "", notes: "" });
  const [patientMode, setPatientMode] = useState("existing");
  const [newPatient, setNewPatient] = useState({ name: "", phone: "", email: "", gender: "", date_of_birth: "" });
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const searchPatients = useCallback(async (query) => {
    const response = await api.get("/clinic/patients-x", { params: { q: query } });
    return Array.isArray(response.data) ? response.data : (response.data?.items || []);
  }, []);
  if (!open) return null;
  const availableServices = form.doctor_id
    ? services.filter((service) => doctorOffersService(doctorServices, form.doctor_id, service.service_id))
    : [];
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (!selectedSlot) { setBusy(false); return; }
      const selectedService = services.find((s)=>s.service_id===form.service_id);
      const payload = { ...form, consultation_mode: selectedService?.service_mode === "hybrid" ? form.consultation_mode : null, appointment_datetime: selectedSlot.start };
      if (patientMode === "new") {
        delete payload.patient_id;
        payload.new_patient = Object.fromEntries(Object.entries(newPatient).filter(([, value]) => value !== ""));
      }
      const created = await api.post("/appointments", payload);
      toast.success("Appointment created");
      if (created.data.notification_delivery?.status !== "sent") toast.info(messagingText(created.data.notification_delivery));
      setForm({ patient_id: "", doctor_id: "", service_id: "", consultation_mode: "", notes: "" }); setSelectedSlot(null);
      setPatientMode("existing"); setNewPatient({ name: "", phone: "", email: "", gender: "", date_of_birth: "" });
      onCreated();
    } catch (err) { toast.error(apiErrorMessage(err, "Unable to create appointment")); }
    setBusy(false);
  };
  return <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Create appointment">
    <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[92vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-mg-navy text-lg">New appointment</h3><button onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button></div>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1" role="tablist" aria-label="Patient selection mode">
          <button type="button" role="tab" aria-selected={patientMode === "existing"} onClick={() => setPatientMode("existing")} className={`rounded-lg px-3 py-2 text-sm font-bold ${patientMode === "existing" ? "bg-white text-mg-navy shadow-sm" : "text-slate-500"}`} data-testid="new-appt-existing-patient">Existing patient</button>
          <button type="button" role="tab" aria-selected={patientMode === "new"} onClick={() => { setPatientMode("new"); setForm({ ...form, patient_id: "" }); }} className={`rounded-lg px-3 py-2 text-sm font-bold ${patientMode === "new" ? "bg-white text-mg-navy shadow-sm" : "text-slate-500"}`} data-testid="new-appt-new-patient"><UserIcon className="w-4 h-4 inline mr-1" /> New patient</button>
        </div>
        {patientMode === "existing" ? <PatientSearchSelect patients={patients} value={form.patient_id} onChange={(patient_id) => setForm({ ...form, patient_id })} onSearch={searchPatients} required testid="new-appt-patient" /> : <div className="rounded-xl border border-mg-medical/20 bg-mg-medical/5 p-3 space-y-3" data-testid="new-appt-new-patient-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldWrap label="Full name *"><input className="mg-input w-full" value={newPatient.name} onChange={(e) => setNewPatient({ ...newPatient, name: e.target.value })} placeholder="Patient full name" data-testid="new-patient-name" /></FieldWrap>
            <FieldWrap label="Mobile number *"><input className="mg-input w-full" value={newPatient.phone} onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })} placeholder="Mobile number" data-testid="new-patient-phone" /></FieldWrap>
            <FieldWrap label="Email"><input type="email" className="mg-input w-full" value={newPatient.email} onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })} placeholder="Email (optional)" /></FieldWrap>
            <FieldWrap label="Gender"><select className="mg-input w-full" value={newPatient.gender} onChange={(e) => setNewPatient({ ...newPatient, gender: e.target.value })}><option value="">Select</option><option>Male</option><option>Female</option><option>Other</option></select></FieldWrap>
            <FieldWrap label="Date of birth"><input type="date" className="mg-input w-full" value={newPatient.date_of_birth} onChange={(e) => setNewPatient({ ...newPatient, date_of_birth: e.target.value })} /></FieldWrap>
          </div>
          <p className="text-xs text-slate-500">The patient record and appointment will be created together. Existing-patient duplicate checks remain enforced.</p>
        </div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SelectField label="Doctor" value={form.doctor_id} onChange={(v) => { setSelectedSlot(null); setForm({ ...form, doctor_id: v, service_id: "", consultation_mode: "" }); }} testid="new-appt-doctor" options={[["", "Select doctor"], ...doctors.map((d) => [d.doctor_id, d.name])]} />
          <SelectField label="Service" value={form.service_id} onChange={(v) => { setSelectedSlot(null); setForm({ ...form, service_id: v, consultation_mode: "" }); }} testid="new-appt-service" options={[["", "Select service"], ...availableServices.map((s) => [s.service_id, `${s.name} — ₹${effectiveBookingPrice(services, doctorServices, form.doctor_id, s.service_id)}`])]} disabled={!form.doctor_id} />
        </div>
        {services.find((s)=>s.service_id===form.service_id)?.service_mode === "hybrid" && <SelectField label="Consultation type" value={form.consultation_mode} onChange={(v)=>setForm({...form,consultation_mode:v})} options={[["","Choose consultation type"],["in_person","In person"],["online","Online"]]} />}
        {form.doctor_id && form.service_id && <AppointmentSlotPicker clinicId={doctors.find((d) => d.doctor_id === form.doctor_id)?.clinic_id} doctorId={form.doctor_id} serviceId={form.service_id} onSelect={setSelectedSlot} />}
        {selectedSlot && <p className="text-sm font-semibold text-emerald-700">Selected: {selectedSlot.local_start?.replace("T", " ").slice(0, 16)} ({selectedSlot.timezone})</p>}
        <FieldWrap label="Notes"><textarea className="mg-input w-full" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FieldWrap>
        <div className="flex justify-end gap-2 pt-3"><button type="button" onClick={onClose} className="mg-btn-secondary">Cancel</button><button disabled={busy || (patientMode === "existing" ? !form.patient_id : (!newPatient.name.trim() || !newPatient.phone.trim())) || !form.doctor_id || !form.service_id || !selectedSlot || (services.find((s)=>s.service_id===form.service_id)?.service_mode === "hybrid" && !form.consultation_mode)} className="mg-btn-primary" data-testid="new-appt-submit">{busy ? "Creating…" : "Create appointment"}</button></div>
      </form>
    </div>
  </div>;
}
